import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import styled from 'styled-components';
import Header from '../../components/common/Header';

// ========================================
// Styled Components
// ========================================

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 0;
  flex: 1;
`;

const Sidebar = styled.nav`
  width: 260px;
  min-width: 260px;
  background: #fff;
  border-right: 1px solid #f0f0f0;
  padding: 20px 0;
`;

const SidebarSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #888;
  padding: 0 20px;
  margin: 0 0 8px 0;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li<{ active?: boolean }>`
  a, button {
    display: block;
    width: 100%;
    padding: 10px 20px;
    font-size: 14px;
    color: ${({ active }) => active ? '#333' : '#666'};
    font-weight: ${({ active }) => active ? '700' : '400'};
    text-decoration: none;
    background: transparent;
    border: none;
    text-align: left;
    cursor: pointer;
    font-family: inherit;

    &:hover {
      background: #f0f0f0;
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 24px 40px;
  background: #fff;
  min-width: 0;
`;

const PublicationSelector = styled.div`
  padding: 12px 20px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e9ecef;
`;

const PublicationLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #888;
  margin-bottom: 6px;
`;

const PublicationSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #00a6c2;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #666;
`;

// ========================================
// GraphQL Queries
// ========================================

const GET_USER_PUBLICATIONS = gql`
  query GetUserPublicationsForSettings {
    activeUser {
      id
      authorizedBrandProfiles {
        id
        name
      }
    }
  }
`;

// ========================================
// Types
// ========================================

interface Publication {
  id: string;
  name: string;
}

interface SettingsNavItem {
  label: string;
  path: string;
  requiresPublication?: boolean;
}

// ========================================
// Navigation Config
// ========================================

const PUBLICATION_NAV_ITEMS: SettingsNavItem[] = [
  { label: 'General', path: 'general', requiresPublication: true },
  { label: 'Staff', path: 'staff', requiresPublication: true },
  { label: 'Teams', path: 'teams', requiresPublication: true },
  { label: 'Workflows', path: 'workflows', requiresPublication: true },
  { label: 'Content Pillars', path: 'pillars', requiresPublication: true },
  { label: 'Audiences', path: 'audiences', requiresPublication: true },
  { label: 'SEO Keywords', path: 'seo-keywords', requiresPublication: true },
  { label: 'Custom Fields', path: 'custom-fields', requiresPublication: true },
  { label: 'Tags', path: 'tags', requiresPublication: true },
  { label: 'Brief Templates', path: 'brief-templates', requiresPublication: true },
  { label: 'Pitch Guidelines', path: 'pitch-guidelines', requiresPublication: true },
  { label: 'Integrations', path: 'integrations', requiresPublication: true },
  { label: 'Plan', path: 'plan', requiresPublication: true },
];

const ACCOUNT_NAV_ITEMS: SettingsNavItem[] = [
  { label: 'Profile', path: '/account/profile' },
  { label: 'Password', path: '/account/password' },
  { label: 'Email Settings', path: '/account/email_settings' },
  { label: 'Invoices', path: '/account/invoices' },
];

const ORG_NAV_ITEMS: SettingsNavItem[] = [
  { label: 'Organization Tags', path: '/organization/tags' },
  { label: 'Organization Users', path: '/organization/users' },
];

// ========================================
// Component
// ========================================

/**
 * SettingsLayout - Container for all settings pages
 * 
 * Provides:
 * - Publication selector
 * - Settings navigation sidebar
 * - Consistent layout for all settings sub-pages
 * 
 * Migrated from contently-contently/app/javascript/backbone/settings/
 */
function SettingsLayout() {
  const { publicationId } = useParams<{ publicationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { data, loading } = useQuery(GET_USER_PUBLICATIONS);
  
  const publications: Publication[] = data?.activeUser?.authorizedBrandProfiles || [];
  
  // Selected publication from URL or first available
  const selectedPublicationId = publicationId || (publications.length > 0 ? publications[0].id : null);
  const selectedPublication = publications.find(p => p.id === selectedPublicationId);
  
  const handlePublicationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPubId = e.target.value;
    // Navigate to same settings section but with different publication
    const pathParts = location.pathname.split('/');
    const settingSection = pathParts[pathParts.length - 1] || 'general';
    navigate(`/publications/${newPubId}/settings/${settingSection}`);
  };
  
  const isPublicationPath = location.pathname.includes('/publications/');
  const isAccountPath = location.pathname.includes('/account/');
  const isOrgPath = location.pathname.includes('/organization/');
  
  const isActivePath = (path: string) => {
    if (path.startsWith('/')) {
      return location.pathname.startsWith(path);
    }
    return location.pathname.endsWith(`/${path}`);
  };

  if (loading) {
    return (
      <PageContainer>
        <Header header="Settings" />
        <LoadingContainer>Loading settings...</LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header header="Settings" />
      <ContentWrapper>
        <Sidebar>
          {/* Publication Selector */}
          {publications.length > 0 && (
            <PublicationSelector>
              <PublicationLabel>Publication</PublicationLabel>
              <PublicationSelect
                value={selectedPublicationId || ''}
                onChange={handlePublicationChange}
              >
                {publications.map(pub => (
                  <option key={pub.id} value={pub.id}>
                    {pub.name}
                  </option>
                ))}
              </PublicationSelect>
            </PublicationSelector>
          )}
          
          {/* Publication Settings */}
          {selectedPublicationId && (
            <SidebarSection>
              <SectionTitle>Publication Settings</SectionTitle>
              <NavList>
                {PUBLICATION_NAV_ITEMS.map(item => (
                  <NavItem 
                    key={item.path} 
                    active={isActivePath(item.path)}
                  >
                    <button onClick={() => 
                      navigate(`/publications/${selectedPublicationId}/settings/${item.path}`)
                    }>
                      {item.label}
                    </button>
                  </NavItem>
                ))}
              </NavList>
            </SidebarSection>
          )}
          
          {/* Account Settings */}
          <SidebarSection>
            <SectionTitle>Account Settings</SectionTitle>
            <NavList>
              {ACCOUNT_NAV_ITEMS.map(item => (
                <NavItem 
                  key={item.path} 
                  active={isActivePath(item.path)}
                >
                  <button onClick={() => navigate(item.path)}>
                    {item.label}
                  </button>
                </NavItem>
              ))}
            </NavList>
          </SidebarSection>
          
          {/* Organization Settings */}
          <SidebarSection>
            <SectionTitle>Organization</SectionTitle>
            <NavList>
              {ORG_NAV_ITEMS.map(item => (
                <NavItem 
                  key={item.path} 
                  active={isActivePath(item.path)}
                >
                  <button onClick={() => navigate(item.path)}>
                    {item.label}
                  </button>
                </NavItem>
              ))}
            </NavList>
          </SidebarSection>
        </Sidebar>
        
        <MainContent>
          <Outlet context={{ 
            publicationId: selectedPublicationId,
            publication: selectedPublication 
          }} />
        </MainContent>
      </ContentWrapper>
    </PageContainer>
  );
}

export default SettingsLayout;
